import {
  MobileVariant,
  Step,
  Stepper,
  SubStep,
} from "@storebrand-digital/insurance-stepper";
import {
  H1,
  MainFlexbox,
  MainHeaderDivider,
  MainHeaderWrapper,
} from "App/global-style";
import {
  getBrandingForStepper,
  getStepCompleteIconForStepper,
} from "Components/Themes/themeUtils";
import React, { useEffect } from "react";
import { Redirect, Route, Switch } from "react-router-dom";
import {
  useCheckPriceSteps,
  useStepToDisplayAsActive,
} from "../../hooks/checkPriceStepHooks";
import { getDefaultPath, getLoginPath } from "../../utils/checkPricePathUtils";
import {
  CustomLeftAlignedMain,
  Paragraph,
  TopLineWrapper,
} from "./productTile.styles";

import { Icons } from "@storebrand-digital/insurance-definitions";
import { Breadcrumb } from "Components/Breadcrumb";
import { SystemErrorAlert } from "Components/Form/Alert/SystemErrorAlert";
import { ShoppingCart } from "Components/ShoppingCart/ShoppingCart";
import { SignedInUser } from "Components/SignedInUser";
import { useTexts } from "Components/TextContext";
import { ThemedSpinner } from "Components/Themes/components/ThemedSpinner";
import { pageDisplayed } from "Store/analytics/analyticsSlice";
import { useBrandingTheme } from "Store/partners/partnerHooks";
import { useStoredProductSelector } from "Store/priceCalculation/priceCalculationSelectors";
import { activateStep } from "Store/steps/stepsSlice";
import { defaultStep } from "Store/steps/stepsUtils";
import { AppPartName } from "Utils/analytics";
import { isDefined } from "Utils/functions";
import { setDocumentTitleForSubPage } from "Utils/navigation/navigationUtils";
import { useUnleashFeatureFlag } from "Utils/unleash";
import { FeatureFlagTask } from "Utils/unleash/flags";
import { useDispatch } from "react-redux";
import { ProductBackendName } from "../../Products/productTypes";
import { useCheckPriceStateCleanup } from "../../hooks/checkPriceHooks";
import { LoginTile } from "../LoginTile";

interface Props {
  readonly title: string;
  readonly description?: string;
  readonly productName: string;
  readonly productBackendName: ProductBackendName;
  readonly productPath: string;
  readonly productSteps: Step[];
  readonly startProductStepId?: string;
  readonly iconName?: Icons;
  readonly shouldSkipLoginStep?: boolean;
  readonly isLoading?: boolean;
  readonly onNavigate?: (step: Step | SubStep) => void;
}

const NoMatch = (): React.ReactElement => <Redirect to="/" />;

export const ProductTile = ({
  title,
  description,
  productName,
  productBackendName,
  productPath,
  productSteps,
  startProductStepId,
  children,
  iconName,
  shouldSkipLoginStep = true,
  isLoading = false,
  onNavigate,
}: React.PropsWithChildren<Props>): React.ReactElement => {
  const featureToggleCustomerAddress = useUnleashFeatureFlag(
    FeatureFlagTask.SSX_988_CUSTOMER_ADDRESS,
  );
  const { getText } = useTexts();
  const dispatch = useDispatch();
  const theme = useBrandingTheme();
  const { steps } = useCheckPriceSteps(
    productSteps,
    startProductStepId,
    productPath,
    shouldSkipLoginStep,
  );
  const { isLoading: isStoredProductLoading, error: storedProductError } =
    useStoredProductSelector();
  const stepToDisplayAsActive = useStepToDisplayAsActive(
    productPath,
    shouldSkipLoginStep,
  );
  useCheckPriceStateCleanup();

  useEffect(() => {
    setDocumentTitleForSubPage(
      getText("documenttitle.checkprice"),
      productName,
    );
    dispatch(pageDisplayed({ pageName: AppPartName.PRODUCT_REDIRECT }));
  }, []);

  return (
    <MainFlexbox>
      <MainHeaderWrapper>
        <Breadcrumb />
        <H1>{title}</H1>
        {isDefined(description) && <Paragraph>{description}</Paragraph>}
        <TopLineWrapper>
          <SignedInUser
            productBackendName={productBackendName}
            featureToggleCustomerAddress={featureToggleCustomerAddress}
          />
          <ShoppingCart />
        </TopLineWrapper>
        <MainHeaderDivider />
      </MainHeaderWrapper>

      <CustomLeftAlignedMain
        noActiveStep={!isDefined(stepToDisplayAsActive)}
        steps={steps}
      >
        <Stepper
          stepCompleteIcon={getStepCompleteIconForStepper(theme.id)}
          disableUpcomingSteps={true}
          desktopAlign="left"
          iconName={iconName}
          desktopContentWidth={100}
          mobileVariant={MobileVariant.VERTICAL_LINE_COMPACT}
          branding={getBrandingForStepper(theme.id)}
          currentStep={stepToDisplayAsActive ?? defaultStep}
          handleNavigate={
            onNavigate ??
            ((step) => {
              dispatch(activateStep(step.id));
            })
          }
          steps={steps}
        >
          {storedProductError ? (
            <SystemErrorAlert alertId="fetch-stored-product" />
          ) : !(isLoading || isStoredProductLoading) ? (
            <Switch>
              <Route
                exact
                path={getDefaultPath(productPath)}
                render={() => <ThemedSpinner />}
              />
              <Route
                exact
                path={getLoginPath(productPath)}
                render={() => (
                  <LoginTile productBackendName={productBackendName} />
                )}
              />
              {children}
              <Route component={NoMatch} />
            </Switch>
          ) : (
            <ThemedSpinner />
          )}
        </Stepper>
      </CustomLeftAlignedMain>
    </MainFlexbox>
  );
};
